/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function UnderwaterChart({ entries, currentPrice }) {
  const [hovered, setHovered] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const wrapRef = useRef(null);
  const [w, setW] = useState(800);

  useEffect(() => {
    const obs = new ResizeObserver(es => setW(es[0].contentRect.width || 800));
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setW(width);
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  const fmt$ = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:2 }).format(v);
  const fmtDate = s => { const [y,m,d]=s.split("-"); return `${d}.${m}.${y.slice(2)}`; };

  // Only underwater trades, sorted deepest first
  const underwater = entries
    .map(e => {
      const buyPrice = e.shares > 0 ? e.dollars / e.shares : 0;
      const depth = (buyPrice - currentPrice) * e.shares; // $ underwater
      const depthPct = buyPrice > 0 ? ((buyPrice - currentPrice) / buyPrice) * 100 : 0;
      return { ...e, buyPrice, depth, depthPct };
    })
    .filter(e => e.depth > 0)
    .sort((a, b) => b.depth - a.depth);

  if (!underwater.length) {
    return (
      <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10,
        padding:"24px", marginBottom:24, textAlign:"center" }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
          letterSpacing:"0.3em", color:"var(--text4)", marginBottom:12 }}>UNDERWATER DEPTH</div>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:14, fontWeight:700,
          color:C.green }}> All trades in profit!</div>
      </div>
    );
  }

  const maxDepth = Math.max(...underwater.map(e => e.depth));
  const LIMIT = 20;
  const visibleUnderwater = showAll ? underwater : underwater.slice(0, LIMIT);
  const hiddenCount = underwater.length - LIMIT;

  const BAR_H = 12;
  const GAP = 3;
  const PAD = { t:4, r:100, b:4, l:72 };
  const H = visibleUnderwater.length * (BAR_H + GAP) + PAD.t + PAD.b;

  const totalUnderwater = underwater.reduce((s, e) => s + e.depth, 0);

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10,
      padding:"16px 20px", marginBottom:24 }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700,
          letterSpacing:"0.3em", color:"var(--text4)" }}>
          UNDERWATER DEPTH · {underwater.length} TRADES
        </div>
        <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:11, fontWeight:700, color:C.red }}>
          {fmt$(totalUnderwater)} total underwater
        </div>
      </div>

      <div ref={wrapRef} style={{ position:"relative" }}>
        <svg width="100%" height={H} viewBox={`0 0 ${w} ${H}`} style={{ display:"block" }}>
          {visibleUnderwater.map((e, i) => {
            const y       = PAD.t + i * (BAR_H + GAP);
            const barW    = Math.max(2, (e.depth / maxDepth) * (w - PAD.l - PAD.r));
            const isHov   = hovered === i;
            const alpha   = 0.4 + (e.depth / maxDepth) * 0.6; // deeper = more opaque
            const color   = `rgba(224,62,26,${isHov ? 1 : alpha})`;

            return (
              <g key={e.id || i}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{ cursor:"default" }}>

                {/* Date label */}
                <text x={PAD.l - 6} y={y + BAR_H/2 + 4} textAnchor="end"
                  fill={isHov ? "var(--text)" : "var(--text4)"}
                  fontSize="8" fontFamily="Montserrat,sans-serif" fontWeight="700">
                  {fmtDate(e.date)}
                </text>

                {/* Bar background */}
                <rect x={PAD.l} y={y} width={w - PAD.l - PAD.r} height={BAR_H}
                  rx="3" fill="var(--bg3)"/>

                {/* Depth bar */}
                <rect x={PAD.l} y={y} width={barW} height={BAR_H}
                  rx="3" fill={color}
                  style={{ transition:"fill 0.15s" }}/>

                {/* Amount label */}
                <text x={w - PAD.r + 6} y={y + BAR_H/2 + 4} textAnchor="start"
                  fill={isHov ? C.red : "var(--text3)"}
                  fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
                  {fmt$(e.depth)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hovered !== null && underwater[hovered] && (() => {
          const e = underwater[hovered];
          const i = hovered;
          const y = PAD.t + i * (BAR_H + GAP);
          const tipY = y > H / 2 ? y - 115 : y + BAR_H + 8;
          return (
            <div style={{
              position:"absolute", left: PAD.l, top: tipY,
              background:"var(--bg2)", border:`1px solid ${C.red}`,
              borderRadius:8, padding:"10px 14px",
              fontFamily:"Montserrat,sans-serif", pointerEvents:"none", zIndex:10, minWidth:220,
            }}>
              <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em",
                color:"var(--text4)", marginBottom:8 }}>
                {fmtDate(e.date)} · #{entries.indexOf(e) + 1}
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 16px" }}>
                {[
                  ["BUY PRICE",  fmt$(e.buyPrice)],
                  ["CURRENT",    fmt$(currentPrice)],
                  ["INVESTED",   fmt$(e.dollars)],
                  ["SHARES",     e.shares.toFixed(4)],
                  ["UNDERWATER", fmt$(e.depth)],
                  ["DEPTH",      `-${e.depthPct.toFixed(1)}%`],
                ].map(([label, val]) => (
                  <div key={label}>
                    <div style={{ fontSize:8, color:"var(--text4)", fontWeight:700, letterSpacing:"0.1em" }}>{label}</div>
                    <div style={{ fontSize:12, fontWeight:700,
                      color: label === "UNDERWATER" || label === "DEPTH" ? C.red : "var(--text)" }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Show more / less button */}
      {!showAll && hiddenCount > 0 && (
        <button onClick={() => setShowAll(true)} style={{
          width:"100%", marginTop:10, background:"rgba(224,62,26,0.07)",
          border:"1px solid rgba(224,62,26,0.3)", borderRadius:7, padding:"8px",
          fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
          letterSpacing:"0.15em", color:C.red, cursor:"pointer", transition:"all 0.15s",
        }}>
          ▼ SHOW {hiddenCount} MORE
        </button>
      )}
      {showAll && hiddenCount > 0 && (
        <button onClick={() => { setShowAll(false); setHovered(null); }} style={{
          width:"100%", marginTop:10, background:"transparent",
          border:"1px solid var(--border)", borderRadius:7, padding:"8px",
          fontFamily:"Montserrat,sans-serif", fontSize:10, fontWeight:700,
          letterSpacing:"0.15em", color:"var(--text4)", cursor:"pointer",
        }}>
          ▲ SHOW LESS
        </button>
      )}
    </div>
  );
}


export default UnderwaterChart;
